import { useState, memo } from "react";
import { Star, Coins, DollarSign, Bookmark, MessageCircle, AlertTriangle, Handshake, Clock, QrCode, Copy, Loader2, History, Store } from "lucide-react";
import { Dialog, DialogContent, DialogTitle } from "@/components/ui/dialog";
import ItemHistoryDialog from "@/components/ItemHistoryDialog";
import { supabase } from "@/integrations/supabase/client";
import logo from "@/assets/logo.webp";
import OwnerActionButtons from "@/components/OwnerActionButtons";
import ShareMenu from "@/components/ShareMenu";
import { isNotified } from "@/lib/notified-sold";
import { Badge } from "@/components/ui/badge";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";
import { useAnuncioPaymentPolling } from "@/hooks/useAnuncioPaymentPolling";
import { format } from "date-fns";
import { ptBR } from "date-fns/locale";
import ReportProblemDialog from "@/components/ReportProblemDialog";
import ContactOptionsPopover from "@/components/ContactOptionsPopover";
import SoldRibbon from "@/components/SoldRibbon";
import SolicitarIntermedioDialog from "@/components/SolicitarIntermedioDialog";
import rubiniCoinsIcon from "@/assets/rubini_coins.gif";
import crystalCoinIcon from "@/assets/crystal_coin.gif";
import { syncFavoritesToServer } from "@/lib/favorites-sync";

interface MarketplaceListItemProps {
  anuncioId: string;
  itemNome: string;
  tipo: "compra" | "venda";
  preco: number;
  moeda: string;
  precoCoins?: number | null;
  precoKk?: number | null;
  servidor: string | null;
  destaque: boolean;
  level?: number | null;
  attack?: number | null;
  defense?: number | null;
  descricao?: string | null;
  createdAt: string;
  expiraEm?: string | null;
  destaqueInicio?: string | null;
  destaqueFim?: string | null;
  temWhatsapp?: boolean;
  temEmail?: boolean;
  upgradeclass?: number | null;
  imageUrl?: string | null;
  apelido?: string | null;
  isCustom?: boolean;
  isOwner?: boolean;
  anuncioStatus?: string;
  sessionContato?: string;
  sessionToken?: string;
  anuncianteId?: string | null;
  anuncianteAvatarUrl?: string | null;
  preferenciasContato?: string[];
  onClick?: () => void;
  onRequireLogin?: (action: () => void) => void;
  onApelidoClick?: (apelido: string) => void;
  onAnuncioAction?: () => void;
  renovacaoPendente?: boolean;
  taxaPaga?: boolean;
  metodoPagamentoTaxa?: string | null;
  hasActiveIntermediacao?: boolean;
  abacatepayUrl?: string | null;
  valorTaxa?: number | null;
  isInteresseMatch?: boolean;
  isRevendedor?: boolean;
}

const getItemImageUrl = (name: string) => {
  const formatted = name.replace(/ /g, "_");
  return `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/proxy-item-image?name=${encodeURIComponent(formatted)}`;
};

const getFavorites = (): string[] => {
  try {
    return JSON.parse(localStorage.getItem("rpgcoin_favorites") || "[]");
  } catch {
    return [];
  }
};

const toggleFavorite = (id: string): boolean => {
  const favs = getFavorites();
  const idx = favs.indexOf(id);
  if (idx >= 0) {
    favs.splice(idx, 1);
    localStorage.setItem("rpgcoin_favorites", JSON.stringify(favs));
    return false;
  }
  favs.push(id);
  localStorage.setItem("rpgcoin_favorites", JSON.stringify(favs));
  return true;
};

const MarketplaceListItem = ({
  anuncioId,
  itemNome,
  tipo,
  preco,
  moeda,
  precoCoins,
  precoKk,
  servidor,
  destaque,
  level,
  attack,
  defense,
  descricao,
  createdAt,
  expiraEm,
  destaqueInicio,
  destaqueFim,
  temWhatsapp,
  temEmail,
  upgradeclass,
  imageUrl,
  apelido,
  isCustom,
  isOwner,
  anuncioStatus,
  sessionContato,
  sessionToken,
  anuncianteId,
  anuncianteAvatarUrl,
  preferenciasContato = ["whatsapp"],
  onClick,
  onRequireLogin,
  onApelidoClick,
  onAnuncioAction,
  renovacaoPendente,
  taxaPaga,
  metodoPagamentoTaxa,
  hasActiveIntermediacao,
  abacatepayUrl,
  valorTaxa,
  isInteresseMatch,
  isRevendedor,
}: MarketplaceListItemProps) => {
  const [isFav, setIsFav] = useState(() => getFavorites().includes(anuncioId));
  const [reportOpen, setReportOpen] = useState(false);
  const [intermedioOpen, setIntermedioOpen] = useState(false);
  const [isReported, setIsReported] = useState(() => isNotified(anuncioId));
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [paymentQr, setPaymentQr] = useState<{ base64: string; text: string | null } | null>(null);
  const [loadingPayment, setLoadingPayment] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const _isDateExpiredEarly = !!(expiraEm && new Date(expiraEm) <= new Date()) && anuncioStatus !== "vendido";
  const isRenewalPending = !!renovacaoPendente && !_isDateExpiredEarly && anuncioStatus !== "aguardando_pagamento";
  const isRenewalPaidByCoins = isRenewalPending && metodoPagamentoTaxa === "coins" && taxaPaga === true;
  const isAwaitingPayment = anuncioStatus === "aguardando_pagamento" || isRenewalPending;
  const { confirmed: paymentConfirmed } = useAnuncioPaymentPolling({
    active: !!paymentQr && isAwaitingPayment,
    anuncioId,
    sessionContato,
    sessionToken,
    onConfirmed: () => {
      // Keep QR dialog mounted briefly so the success tick stays visible,
      // then clear and refresh the listing.
      setTimeout(() => {
        setPaymentQr(null);
        onAnuncioAction?.();
      }, 1500);
    },
  });

  const handlePayClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    e.preventDefault();
    if (loadingPayment) return;
    setLoadingPayment(true);
    try {
      const sess = JSON.parse(localStorage.getItem("rpgcoin_client_session") || "{}");
      if (isRenewalPending) {
        const { data, error } = await supabase.functions.invoke("client-anuncio-action", {
          body: { contato: sess.contato, token: sess.token, anuncio_id: anuncioId, acao: "regerar_pagamento" },
        });
        if (!error && data?.pix_qr_code_base64) {
          setPaymentQr({ base64: data.pix_qr_code_base64, text: data.pix_qr_code || null });
        } else {
          toast({ title: data?.error || "Erro ao gerar QR Code", variant: "destructive" });
        }
      } else {
        const { data, error } = await supabase.functions.invoke("create-checkout", {
          body: { transacao_id: anuncioId, metodo: "pix", contato: sess.contato, token: sess.token },
        });
        if (!error && data?.qr_code_base64) {
          setPaymentQr({ base64: data.qr_code_base64, text: data.qr_code || null });
        } else {
          toast({ title: "Erro ao gerar QR Code", variant: "destructive" });
        }
      }
    } catch {
      toast({ title: "Erro ao gerar pagamento", variant: "destructive" });
    }
    setLoadingPayment(false);
  };

  const handleFavorite = (e: React.MouseEvent) => {
    e.stopPropagation();
    const doFav = () => {
      const added = toggleFavorite(anuncioId);
      setIsFav(added);
      syncFavoritesToServer();
      toast({ title: added ? "Adicionado à lista!" : "Removido da lista" });
    };
    if (onRequireLogin) onRequireLogin(doFav);
    else doFav();
  };

  // Removed direct WhatsApp handler - now handled via ContactOptionsPopover + get-contact-info


  const handleReportClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    const doOpen = () => setReportOpen(true);
    if (onRequireLogin) onRequireLogin(doOpen);
    else doOpen();
  };

  const formattedDate = (() => {
    try {
      return format(new Date(createdAt), "dd/MM/yy HH:mm", { locale: ptBR });
    } catch {
      return "";
    }
  })();

  const isPaused = anuncioStatus === "pausado";
  const isDateExpired = !!(expiraEm && new Date(expiraEm) <= new Date()) && anuncioStatus !== "vendido";

  // Destaque→geral downgrade: owner-only "ghost" preview mirrored into the general list until destaque_fim.
  const destaqueFimDate = destaqueFim ? new Date(destaqueFim) : null;
  const isMirrorPreview = !!(
    isOwner &&
    !destaque &&
    destaqueFimDate &&
    !Number.isNaN(destaqueFimDate.getTime()) &&
    destaqueFimDate.getTime() > Date.now()
  );
  const mirrorLabel = isMirrorPreview
    ? destaqueFimDate!.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : null;

  return (
    <>
    <div
      onClick={onClick}
      className={`tibia-border bg-card rounded-lg overflow-hidden cursor-pointer flex items-center gap-3 px-3 py-2 relative transition-all duration-200 hover:translate-x-0.5 active:scale-[0.98] active:opacity-70 ${destaque ? "gold-glow" : ""} ${isInteresseMatch ? "interesse-match-card" : ""}`}
      style={{ contentVisibility: "auto", containIntrinsicSize: "0 60px" }}
    >
      {isInteresseMatch ? (
        <div className="interesse-badge">Interesse</div>
      ) : isRevendedor ? (
        <div className="revendedor-badge" title="Revendedor">Revendedor</div>
      ) : null}
      {isInteresseMatch && isRevendedor && (
        <div className="revendedor-corner-badge top-1 right-1" title="Revendedor">Revendedor</div>
      )}
      {/* Mirror preview overlay (destaque→geral downgrade, owner-only) */}
      {isMirrorPreview && (
        <div
          onClick={(e) => e.stopPropagation()}
          className="absolute inset-0 z-30 flex flex-col items-center justify-center gap-0.5 px-3 text-center bg-background/85 rounded-lg backdrop-blur-[1px] cursor-default"
        >
          <span className="font-pixel text-[7px] uppercase tracking-wide text-primary-foreground bg-primary/90 px-2 py-0.5 rounded-sm shadow">
            Pré-visualização
          </span>
          <span className="text-[10px] font-medium text-foreground leading-tight">
            Aparecerá na lista a partir de <span className="font-bold text-primary">{mirrorLabel}</span>
          </span>
          <span className="text-[8px] text-muted-foreground italic">
            Visível apenas para você
          </span>
        </div>
      )}
      {/* Active intermediation overlay */}
      {hasActiveIntermediacao && (
        <div className="absolute inset-0 bg-purple-900/40 z-10 rounded-lg flex items-center justify-center pointer-events-none">
          <div className="bg-purple-500/90 px-3 py-1 rounded-sm flex items-center gap-1.5">
            <Handshake className="w-3.5 h-3.5 text-white" />
            <span className="font-pixel text-[7px] text-white tracking-wider font-bold">EM INTERMÉDIO</span>
          </div>
        </div>
      )}
      {/* Paused overlay */}
      {isPaused && !isDateExpired && (
        <div className="absolute inset-0 bg-black/50 z-10 rounded-lg flex items-center justify-center pointer-events-none">
          <div className="bg-yellow-500/90 px-3 py-0.5 rounded-sm">
            <span className="font-pixel text-[7px] text-black tracking-wider font-bold">PAUSADO</span>
          </div>
        </div>
      )}
      {/* Expired by date overlay */}
      {isDateExpired && anuncioStatus !== "pendente" && anuncioStatus !== "aguardando_pagamento" && (
        <div className="absolute inset-0 bg-black/60 z-10 rounded-lg flex items-center justify-center pointer-events-none">
          <div className="bg-muted/90 px-3 py-0.5 rounded-sm">
            <span className="font-pixel text-[7px] text-muted-foreground tracking-wider font-bold">ENCERRADO</span>
          </div>
        </div>
      )}
      {/* Pending approval overlay */}
      {anuncioStatus === "pendente" && (
        <div className="absolute inset-0 bg-amber-900/40 z-10 rounded-lg flex items-center justify-center pointer-events-none">
          <div className="bg-amber-500/90 px-3 py-1 rounded-sm flex items-center gap-1.5">
            <Clock className="w-3.5 h-3.5 text-black" />
            <span className="font-pixel text-[7px] text-black tracking-wider font-bold">AGUARDANDO APROVAÇÃO</span>
          </div>
        </div>
      )}
      {/* Awaiting payment overlay */}
      {anuncioStatus === "aguardando_pagamento" && (
        <div className="absolute inset-0 bg-amber-900/40 z-10 rounded-lg flex flex-col items-center justify-center gap-1.5">
          <div className="bg-amber-500/90 px-3 py-1 rounded-sm flex items-center gap-1.5 pointer-events-none">
            <Clock className="w-3.5 h-3.5 text-black" />
            <span className="font-pixel text-[7px] text-black tracking-wider font-bold">AGUARDANDO PAGAMENTO</span>
          </div>
          {abacatepayUrl && (
            <button
              onClick={handlePayClick}
              disabled={loadingPayment}
              className="pointer-events-auto flex items-center gap-1 px-2.5 py-0.5 rounded-sm text-[7px] font-pixel bg-primary/90 text-primary-foreground border border-primary/60 hover:bg-primary transition-colors shadow-md disabled:opacity-50"
            >
              {loadingPayment ? <Loader2 className="w-3 h-3 animate-spin" /> : <QrCode className="w-3 h-3" />}
              {loadingPayment ? "Gerando..." : "Pagar"}
            </button>
          )}
        </div>
      )}
      {/* Renewal pending overlay (blue) - only visible to owner */}
      {isRenewalPending && isOwner && (
        <div className="absolute inset-0 bg-blue-900/40 z-10 rounded-lg flex flex-col items-center justify-center gap-1.5">
          <div className="bg-blue-500/90 px-3 py-1 rounded-sm flex flex-col items-center gap-0.5 pointer-events-none">
            <div className="flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5 text-white" />
              <span className="font-pixel text-[7px] text-white tracking-wider font-bold">RENOVAÇÃO PENDENTE</span>
            </div>
            <span className="font-pixel text-[6px] text-white/80 tracking-wider">
              {isRenewalPaidByCoins ? "Aguardando aprovação" : "Aguardando pagamento"}
            </span>
          </div>
          <div className="flex flex-col items-center gap-1">
            {!isRenewalPaidByCoins && (
              <button
                onClick={handlePayClick}
                disabled={loadingPayment || actionLoading === "cancelar_renovacao"}
                className="pointer-events-auto flex items-center gap-1 px-2.5 py-0.5 rounded-sm text-[7px] font-pixel bg-primary/90 text-primary-foreground border border-primary/60 hover:bg-primary transition-colors shadow-md disabled:opacity-50"
              >
                {loadingPayment ? <Loader2 className="w-3 h-3 animate-spin" /> : <QrCode className="w-3 h-3" />}
                {loadingPayment ? "Gerando..." : "Pagar Renovação"}
              </button>
            )}
            <button
              onClick={async (e) => {
                e.stopPropagation();
                e.preventDefault();
                if (actionLoading) return;
                setActionLoading("cancelar_renovacao");
                try {
                  const sess = JSON.parse(localStorage.getItem("rpgcoin_client_session") || "{}");
                  const { data, error } = await supabase.functions.invoke("client-anuncio-action", {
                    body: { contato: sess.contato, token: sess.token, anuncio_id: anuncioId, acao: "cancelar_renovacao" },
                  });
                  if (error || data?.error) {
                    toast({ title: data?.error || "Erro ao cancelar renovação", variant: "destructive" });
                  } else {
                    toast({ title: "Renovação cancelada" });
                    setPaymentQr(null);
                    onAnuncioAction?.();
                  }
                } catch {
                  toast({ title: "Erro ao cancelar renovação", variant: "destructive" });
                } finally {
                  setActionLoading(null);
                }
              }}
              disabled={!!actionLoading}
              className="pointer-events-auto flex items-center gap-1 px-2.5 py-0.5 rounded-sm text-[7px] font-pixel bg-destructive/90 text-destructive-foreground border border-destructive/60 hover:bg-destructive transition-colors shadow-md disabled:opacity-50"
            >
              {actionLoading === "cancelar_renovacao" ? <Loader2 className="w-3 h-3 animate-spin" /> : null}
              Cancelar
            </button>
          </div>
        </div>
      )}
      {/* QR Code payment dialog */}
      <Dialog open={!!paymentQr && (isAwaitingPayment || paymentConfirmed)} onOpenChange={(open) => { if (!open && !paymentConfirmed) setPaymentQr(null); }}>
        <DialogContent className="max-w-xs" hideCloseButton>
          <DialogTitle className="sr-only">Pagamento PIX</DialogTitle>
          {paymentConfirmed ? (
            <div className="flex flex-col items-center gap-3 animate-fade-in py-4">
              <div className="w-14 h-14 rounded-full bg-green-500/20 border-2 border-green-500 flex items-center justify-center">
                <svg className="w-8 h-8 text-green-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
              </div>
              <span className="font-pixel text-[11px] text-green-500 font-bold">Pagamento Confirmado!</span>
              <span className="font-pixel text-[9px] text-muted-foreground">Anúncio sendo processado.</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 py-2">
              <div className="flex items-center gap-2">
                <img
                  src={imageUrl || getItemImageUrl(itemNome)}
                  alt={itemNome}
                  width={28}
                  height={28}
                  className="w-7 h-7 object-contain"
                  loading="lazy"
                  decoding="async"
                  onError={(e) => { (e.currentTarget as HTMLImageElement).style.visibility = "hidden"; }}
                />
                <span className="font-pixel text-[10px] text-foreground truncate max-w-[180px]">
                  {itemNome}
                </span>
              </div>
              <span className="font-pixel text-[13px] text-green-400 font-bold drop-shadow-[0_0_4px_rgba(74,222,128,0.5)]">
                R$ {Number(valorTaxa || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
              </span>
              <span className="font-pixel text-[9px] text-muted-foreground">Taxa do anúncio</span>
              <div className="relative">
                <img
                  src={`data:image/png;base64,${paymentQr?.base64}`}
                  alt="QR Code PIX"
                  className="w-48 h-48 rounded-sm border border-border"
                />
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-9 h-9 rounded-full bg-white flex items-center justify-center shadow-md">
                    <img src={logo} alt="Logo" className="w-7 h-7 rounded-full object-cover" />
                  </div>
                </div>
              </div>
              {paymentQr?.text && (
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(paymentQr.text!);
                    toast({ title: "Código PIX copiado!" });
                  }}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-sm text-[9px] font-pixel bg-primary/10 text-primary hover:bg-primary/20 transition-colors"
                >
                  <Copy className="w-3.5 h-3.5" />
                  Copiar código
                </button>
              )}
              <div className="flex items-center gap-1.5">
                <Loader2 className="w-3.5 h-3.5 animate-spin text-muted-foreground" />
                <span className="text-[9px] text-muted-foreground">Aguardando pagamento...</span>
              </div>
              <button
                onClick={() => setPaymentQr(null)}
                className="text-[9px] font-pixel text-muted-foreground hover:text-foreground transition-colors"
              >
                Fechar
              </button>
            </div>
          )}
        </DialogContent>
      </Dialog>
      {/* History button + Image container */}
      <div className="flex items-center gap-1">
        <button
          onClick={(e) => { e.stopPropagation(); setHistoryOpen(true); }}
          className="flex-shrink-0 p-1.5 rounded-md bg-transparent hover:scale-110 transition-all"
          title="Histórico do item"
        >
          <History className="w-5 h-5 text-border drop-shadow-[0_0_2px_hsl(var(--gold)/0.5)]" />
        </button>
        {/* Image */}
        <div className="flex-shrink-0 w-12 h-12 rounded flex items-center justify-center relative">
          {isCustom && (
            <Badge className="absolute -top-1 -right-1 bg-purple-600/80 text-white border-purple-500/50 font-pixel text-[4px] px-0.5 py-0 rounded-sm z-10">C</Badge>
          )}
          {upgradeclass != null && upgradeclass > 0 && (
            <div className="absolute -bottom-1 -right-1 w-4 h-4 rounded-full bg-[#FF8C00] flex items-center justify-center border border-[#FF6B00] shadow-sm z-10">
              <span className="font-pixel text-[7px] font-bold text-white">{upgradeclass}</span>
            </div>
          )}
          <img
            src={getItemImageUrl(itemNome)}
            alt={itemNome}
            width={40}
            height={40}
            className="max-w-[40px] max-h-[40px] object-contain image-rendering-pixelated"
            onError={(e) => { (e.target as HTMLImageElement).src = "/placeholder.svg"; }}
            loading="lazy"
            decoding="async"
          />
        </div>
      </div>

      {/* Type badge */}
      <div className={`flex-shrink-0 px-2 py-1 rounded font-pixel text-[7px] tracking-wider ${
        tipo === "venda" ? "bg-red-600/30 text-red-400" : "bg-blue-600/30 text-blue-400"
      }`}>
        {tipo === "venda" ? "VENDA" : "COMPRA"}
      </div>

       {/* Name + details */}
       <div className="flex-1 min-w-0">
         <div className="flex items-center gap-2">
           <h3 className="font-pixel text-[9px] text-primary truncate" title={itemNome}>
             {itemNome}
           </h3>
           {destaque && <Star className="w-3 h-3 text-primary fill-primary flex-shrink-0" />}
         </div>
          <div className="flex items-center gap-3 mt-0.5">
            {servidor && (
              <span className="text-[7px] text-muted-foreground truncate font-bold">🌐 {servidor}</span>
            )}
            {descricao && (
             <span className="text-[7px] text-muted-foreground truncate max-w-[200px]" title={descricao}>{descricao}</span>
           )}
         </div>
       </div>


      {/* Price */}
      <div className="flex-shrink-0 text-right">
        {preco === 0 && (!precoCoins || precoCoins === 0) && (!precoKk || precoKk === 0) ? (
          <div className="flex items-center gap-0.5 justify-end">
            <MessageCircle className="w-3 h-3 text-blue-400" />
            <span className="font-pixel text-[7px] text-blue-400">Recebendo propostas...</span>
          </div>
        ) : (
          <>
            {moeda === "brl" && preco > 0 && (
              <div className="flex items-center gap-0.5 justify-end">
                <span className="font-pixel text-[8px] text-green-400 font-bold shrink-0 drop-shadow-[0_0_4px_rgba(74,222,128,0.5)]">R$</span>
                <span className="font-pixel text-[8px] text-primary">
                  {preco.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}
                </span>
              </div>
            )}
            {((precoCoins != null && precoCoins > 0) || (moeda === "coins" && preco > 0)) ? (
              <div className="flex items-center gap-0.5 justify-end">
                <img src={rubiniCoinsIcon} alt="RC" className="w-3 h-3" />
                <span className="font-pixel text-[8px] text-primary">
                  {(precoCoins || preco).toLocaleString("pt-BR")}
                </span>
              </div>
            ) : null}
            {precoKk != null && precoKk > 0 && (
              <div className="flex items-center gap-0.5 justify-end">
                <img src={crystalCoinIcon} alt="KK" className="w-3 h-3" />
                <span className="font-pixel text-[8px] text-amber-400">
                  {precoKk.toLocaleString("pt-BR")}
                </span>
              </div>
            )}
          </>
        )}
      </div>

       {/* Server + Date + Apelido */}
        <div className="hidden md:flex flex-col items-end flex-shrink-0 gap-0.5">
          <span className="text-[6px] text-muted-foreground">{formattedDate}</span>
          {servidor && <span className="text-[7px] text-muted-foreground font-bold">🌐 {servidor}</span>}
          {apelido && (
            <Badge
              variant="outline"
              title={isRevendedor ? "Revendedor" : undefined}
              className={`font-pixel text-[5px] px-1 py-0 rounded-sm cursor-pointer transition-colors inline-flex items-center gap-0.5 ${
                isRevendedor
                  ? "bg-indigo-600/90 border-indigo-400 text-white hover:bg-indigo-500 shadow-[0_0_6px_hsl(231_85%_60%/0.6)]"
                  : "border-primary/30 text-primary/80 hover:border-primary hover:text-primary"
              }`}
              onClick={(e) => { e.stopPropagation(); onApelidoClick?.(apelido); }}
            >
              {isRevendedor && <Store className="w-2 h-2" />}
              {apelido}
            </Badge>
          )}
       </div>

      {/* Actions */}
      <div className={`relative z-40 flex items-center gap-1 flex-shrink-0 border-l pl-2 ml-1 ${isDateExpired ? "pointer-events-none opacity-50" : ""} ${isOwner ? `border-amber-400/30 bg-amber-300/20 rounded-r-lg -mr-3 pr-3 -my-2 py-2` : "border-border/40"}`}>
        {isOwner ? (
          <OwnerActionButtons
            anuncioId={anuncioId}
            anuncioStatus={anuncioStatus}
            variant="inline"
            expiraEm={expiraEm}
            isDestaque={destaque}
            destaqueInicio={destaqueInicio || null}
            sessionContato={sessionContato}
            sessionToken={sessionToken}
            actionLoading={actionLoading}
            setActionLoading={setActionLoading}
            onAnuncioAction={onAnuncioAction}
            renovacaoPendente={renovacaoPendente}
            itemNome={itemNome}
            itemImageUrl={getItemImageUrl(itemNome)}
          />
        ) : (
          <>
            <ContactOptionsPopover
              anuncioId={anuncioId}
              itemNome={itemNome}
              temWhatsapp={temWhatsapp}
              temEmail={temEmail}
              anuncianteId={anuncianteId}
              anuncianteApelido={apelido}
              anuncianteAvatarUrl={anuncianteAvatarUrl}
              preferenciasContato={preferenciasContato}
              onRequireLogin={onRequireLogin}
              sessionContato={sessionContato}
              sessionToken={sessionToken}
            >
              <button className="p-1 hover:scale-110 transition-all">
                <MessageCircle className="w-3 h-3 text-green-500/70 hover:text-green-400" />
              </button>
            </ContactOptionsPopover>
            <button onClick={(e) => { e.stopPropagation(); const doOpen = () => setIntermedioOpen(true); if (onRequireLogin) onRequireLogin(doOpen); else doOpen(); }} disabled={hasActiveIntermediacao} className="p-1 hover:scale-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100" title={hasActiveIntermediacao ? "Em intermédio" : "Solicitar intermédio"}>
              <Handshake className="w-3 h-3 text-purple-400/70 hover:text-purple-400" />
            </button>
            <button onClick={handleFavorite} className="p-1 hover:scale-110 transition-all">
              <Bookmark className={`w-3 h-3 ${isFav ? "fill-primary text-primary" : "text-amber-400/70 hover:text-amber-400"}`} />
            </button>
            <ShareMenu
              url={`${window.location.origin}/item/${anuncioId}`}
              text={`Confira "${itemNome}" no RPG Coin!`}
              iconClassName="w-3 h-3 text-blue-400/70 hover:text-blue-400"
            />
            <button onClick={handleReportClick} disabled={isReported} className="p-1 hover:scale-110 transition-all disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100" title={isReported ? "Já reportado" : "Reportar problema"}>
              <AlertTriangle className="w-3 h-3 text-red-400/70 hover:text-red-400" />
            </button>
          </>
        )}
      </div>

    </div>

    <ReportProblemDialog
      open={reportOpen}
      onOpenChange={setReportOpen}
      anuncioId={anuncioId}
      itemNome={itemNome}
      tier={upgradeclass}
      servidor={servidor}
      onReported={() => setIsReported(true)}
    />

    {sessionContato && sessionToken && (
      <SolicitarIntermedioDialog
        open={intermedioOpen}
        onOpenChange={setIntermedioOpen}
        anuncioId={anuncioId}
        itemNome={itemNome}
        preco={preco}
        moeda={moeda}
        precoCoins={precoCoins}
        precoKk={precoKk}
        servidor={servidor}
        descricao={descricao}
        anuncianteApelido={apelido}
        anuncianteAvatarUrl={anuncianteAvatarUrl}
        itemImageUrl={imageUrl || getItemImageUrl(itemNome)}
        sessionContato={sessionContato}
        sessionToken={sessionToken}
      />
    )}

    <ItemHistoryDialog
      open={historyOpen}
      onOpenChange={setHistoryOpen}
      itemNome={itemNome}
      sessionContato={sessionContato}
      sessionToken={sessionToken}
      onRequireLogin={onRequireLogin}
      intermediatingAnuncios={hasActiveIntermediacao ? new Set([anuncioId]) : undefined}
    />
    </>
  );
};

export default memo(MarketplaceListItem);
